import { globalFilters as Filters } from 'collections/filters';
import { addTags, resetTags } from 'streams/tags/actions';
import { loadCachedDynamicFilters } from 'presentation/dynamicFilterCache';
import * as profiling from '@ardoq/profiling';
import { getActiveScenarioId } from 'streams/activeScenario/activeScenario$';
import { dispatchAction } from '@ardoq/rxbeach';
import { setScenarioId, updateScopeDiff } from 'scope/actions';
import {
  setSort,
  setScenarioId as setScenarioIdContext,
  closeAllWorkspacesSilently,
  justCloseWorkspaces,
} from 'streams/context/ContextActions';
import { getScenario } from 'presentation/presentationData';
import { dataInterface } from '@ardoq/data-interface';
import { logError, logWarn } from '@ardoq/logging';
import { slideOperations } from 'streams/slides/slideOperations';
import { ScenarioSlideErrorAlert as ScenarioSlideErrorDialog } from './SlideLoadErrorDialog';
import { getDataRelevantToWorkspaces } from './utils';
import { isInScopeDiffMode } from 'scope/scopeDiff';
import { handleDiffContext } from 'scope/routines';
import { setGroupBys } from 'presentation/viewPane/utils';
import { setIsUpdatingAppState } from '../../isUpdatingAppState$';
import { updateLinkedWorkspaces } from 'models/actions';
import {
  notifyWorkspaceAggregatedLoaded,
  markWorkspaceAggregatedLoaded,
} from 'streams/workspaces/actions';
import { uniq } from 'lodash';
import {
  APIScopeData,
  APIScopeDataWithExplicitParentChildReferences,
  ArdoqId,
  EnhancedPresentation,
  VisualizationSlide,
  LoadedState,
} from '@ardoq/api-types';
import { requestMainView } from 'streams/views/mainContent/requestMainView';
import { toRenderedViewpointViewId } from 'views/environmentals/getViewpointModeSupportedViews';
import { fixPresentationScenario } from 'scopeData/utils/fixScopeData';
import {
  clearLoadedGraphState,
  replaceScopeComponentIds,
} from 'streams/traversals/actions';
import { toComponentPaths } from 'traversals/toFEComponentPaths';
import { scopeDataOperations } from '@ardoq/scope-data';
import { contextInterface } from 'modelInterface/contextInterface';
import { workspaceInterface } from '@ardoq/workspace-interface';
import {
  clearComponent,
  loadWorkspacesAndSetContext,
  setWorkspaceIds,
  switchToWorkspace,
} from '../../streams/context/ContextActions';
import { addFields, resetFields } from 'streams/fields/actions';
import {
  addReferences,
  resetReferences,
} from 'streams/references/ReferenceActions';
import {
  addComponentsAction,
  resetComponentsAction,
} from 'streams/components/ComponentActions';
import { componentInterface } from '@ardoq/component-interface';
import { resetGraph } from 'modelInterface/graphModelActions';
import { zip } from '@ardoq/common-helpers';
import { applyPathCollapsingRules } from 'traversals/applyPathCollapsingRules.2026';
import {
  loadedStateOperations,
  fromPersistedLoadedState,
} from 'loadedState/loadedStateOperations';

async function setComponent(
  componentId: ArdoqId | undefined | null,
  componentName: string | undefined | null
) {
  const matchedId =
    (componentId && componentInterface.get(componentId)?._id) ||
    componentInterface.getAllComponents().find(c => c.name === componentName)
      ?._id;
  if (matchedId) {
    return contextInterface.setComponentById(matchedId);
  }
  dispatchAction(clearComponent());
}

function isCorrectWorkspaces(currentWsIds: ArdoqId[], wsIds: ArdoqId[]) {
  return (
    currentWsIds.length > 0 &&
    !currentWsIds.some(currentWsId => wsIds.indexOf(currentWsId) === -1) &&
    !wsIds.some(wsId => currentWsIds.indexOf(wsId) === -1)
  );
}

const resetCollectionData = () => {
  if (isInScopeDiffMode()) {
    dispatchAction(updateScopeDiff({ scopeDiff: null }));
  }
  dispatchAction(justCloseWorkspaces());
  dataInterface.clearCollections();
};

const toScopeWorkspaceIds = ({
  workspaces,
}: Pick<APIScopeData, 'workspaces'>): ArdoqId[] =>
  uniq(workspaces.map(({ _id }) => _id));

const setWorkspacesToBeLoaded = (workspaceIds: ArdoqId[]) => {
  workspaceIds.forEach(workspaceId =>
    dispatchAction(markWorkspaceAggregatedLoaded({ workspaceId }))
  );
  if (workspaceIds[0]) {
    dispatchAction(
      loadWorkspacesAndSetContext({
        contextWorkspaceId: workspaceIds[0],
        workspaceIds,
      })
    );
  }
};

const getLoadedStates = (
  presentationData: EnhancedPresentation,
  slideId: ArdoqId
): LoadedState[] => {
  try {
    const traversalExecuteResult = presentationData.traversalExecute?.[slideId];
    const slide = presentationData.authorizedSlides?.find(
      slide => slide._id === slideId
    );
    if (!(slide && traversalExecuteResult)) {
      logError(Error('Missing traversal execute result for slide'));
      return [];
    }

    return zip(
      (slide.loadedStates ?? []).map(fromPersistedLoadedState),
      traversalExecuteResult.loadedStates
    ).map(
      ([
        loadedStateParams,
        {
          paths,
          startSetResult,
          pathResults,
          scopeData: rawScopeData = scopeDataOperations.getEmpty(),
        },
      ]) => {
        const { scopeData, parentChildReferences } =
          scopeDataOperations.partitionParentChildReferences(
            removeDanglingReferences(rawScopeData)
          );
        const componentIdsAndReferences = {
          componentIds: scopeData.scopeComponents,
          references: scopeData.references,
          parentChildReferences,
          startSetResult: startSetResult,
          pathResults: toComponentPaths(
            paths,
            pathResults ?? [],
            rawScopeData.references
          ),
        };

        return loadedStateOperations.attachLoadedGraph(loadedStateParams, {
          scopeData,
          componentIdsAndReferences,
        });
      }
    );
  } catch (e) {
    logError(Error('Failed to build loaded states for slide'));
    return [];
  }
};

// We can get invalid scope data from the backend, where references are not
// connected to components in scope data. This function removes those references.
// This can happen because the loaded states could contain dangling references.
// If https://ardoqcom.atlassian.net/browse/ARD-25645 is implemented, we can
// remove this function.
const removeDanglingReferences = (
  scopeData: APIScopeDataWithExplicitParentChildReferences
) => {
  const componentIdSet = new Set(scopeData.components.map(({ _id }) => _id));
  const references = scopeData.references.filter(
    ({ source, target }) =>
      componentIdSet.has(source) && componentIdSet.has(target)
  );
  return { ...scopeData, references };
};

const tryLoadTraversalData = (
  presentation: EnhancedPresentation,
  slideId: ArdoqId
) => {
  const loadedStates = getLoadedStates(presentation, slideId);
  if (loadedStates.length === 0) {
    return false;
  }

  const scopeData = scopeDataOperations.merge(
    ...loadedStates.map(({ scopeData }) => scopeData)
  );

  const collapsedResult = applyPathCollapsingRules(loadedStates);

  const inputScopeComponentsCount = loadedStates.reduce(
    (sum, ls) => sum + ls.componentIdsAndReferences.componentIds.length,
    0
  );
  if (
    inputScopeComponentsCount > 0 &&
    collapsedResult.scopeComponentIds.length === 0
  ) {
    logWarn(
      Error('Slide loaded states collapsed to empty scopeComponentIds'),
      'Slide had non-empty scopeComponents pre-collapse but post-collapse is empty — view will render blank',
      {
        slideId,
        inputScopeComponentsCount,
        loadedStatesCount: loadedStates.length,
      }
    );
  }

  const enhancedScopeData = scopeDataOperations.enhance(scopeData, {
    graphReferenceIds: collapsedResult.scopeReferenceIds,
    virtualReferences: collapsedResult.newReferences,
    shouldFreeze: false, // freezing the models will throw an error in the "fixModel" function
  });

  // The entity streams are projections of the enhanced scope data, including
  // the virtual collapsed-path references and the synthetic virtual model.
  dataInterface.resetWithScopeData(enhancedScopeData);

  setWorkspacesToBeLoaded(toScopeWorkspaceIds(enhancedScopeData));
  dispatchAction(
    replaceScopeComponentIds({
      scopeComponentIds: collapsedResult.scopeComponentIds,
      scopeReferenceIds: collapsedResult.scopeReferenceIds,
      parentChildReferences: collapsedResult.parentChildReferences,
      hierarchyDefinition:
        loadedStateOperations.toPathsWithStartSetResultsAndCollapsedPaths(
          collapsedResult.loadedStates
        ),
      scopeData: enhancedScopeData,
    })
  );

  return true;
};

const loadScenario = (
  presentation: EnhancedPresentation,
  scenarioId: ArdoqId | null,
  isInVisualDiffMode: boolean
) => {
  if (!scenarioId) {
    return;
  }
  const scenario = getScenario(presentation, scenarioId);
  if (!scenario) {
    logError(Error('Scenario not found.'), null, { scenarioId });
    ScenarioSlideErrorDialog();
    return;
  }

  dispatchAction(justCloseWorkspaces());
  const fixedScenario = fixPresentationScenario(scenario);
  const { master, branch, branchOff } = fixedScenario.diffContext || {};
  const scopeData = isInVisualDiffMode ? branch : fixedScenario.scope;

  dataInterface.resetWithScopeData(scopeData, { silent: false });

  if (isInVisualDiffMode) {
    handleDiffContext({
      master,
      branch,
      branchOff,
    });
  }

  setWorkspacesToBeLoaded(toScopeWorkspaceIds(scopeData));
};

const postLoad = async (
  slide: VisualizationSlide,
  scenarioId: ArdoqId | null
) => {
  const selectedScenarioId = slide.selectedScenarioId;
  const componentId = slide.componentId;
  const componentName = slide.componentName;
  dispatchAction(switchToWorkspace({ workspaceId: slide.workspaceId }));
  await setComponent(componentId, componentName);

  if (selectedScenarioId && selectedScenarioId === scenarioId) {
    dispatchAction(setScenarioIdContext(scenarioId));
  }

  loadCachedDynamicFilters(slide.dynamicFilters || []);
  Filters.loadFilters({
    ...slide.filters,
    shouldTriggerChangeEvent: true,
  });
  setGroupBys(slide.groupBys || []);
  const sort = slideOperations.getSort(slide);
  if (sort) {
    dispatchAction(setSort(sort));
  }

  setTimeout(() => dispatchAction(setIsUpdatingAppState(false)), 50);
};

const reset = (
  presentation: EnhancedPresentation,
  workspaceIds: ArdoqId[],
  consideredWorkspaceIds: ArdoqId[]
) => {
  Filters.reset();
  dispatchAction(closeAllWorkspacesSilently());

  dispatchAction(resetComponentsAction());
  dispatchAction(resetGraph());
  dispatchAction(resetReferences());
  dispatchAction(resetTags());
  dispatchAction(resetFields());

  dispatchAction(
    updateLinkedWorkspaces({
      workspaces: presentation.workspaces,
      references: presentation.references,
    })
  );

  const { components, references, tags } = getDataRelevantToWorkspaces(
    presentation,
    new Set(workspaceIds),
    new Set(consideredWorkspaceIds)
  );

  dispatchAction(addComponentsAction({ components }));
  dispatchAction(addTags({ tags }));
  dispatchAction(addReferences({ references }));
  dispatchAction(addFields({ fields: presentation.fields }));

  workspaceIds.forEach(workspaceId => {
    dispatchAction(markWorkspaceAggregatedLoaded({ workspaceId }));
    dispatchAction(notifyWorkspaceAggregatedLoaded({ workspaceId }));
  });

  const existingWorkspaceIds = workspaceIds.filter(
    workspaceInterface.workspaceExists
  );
  dispatchAction(setWorkspaceIds(existingWorkspaceIds));
};

const loadMasterData = (
  presentation: EnhancedPresentation,
  workspaceIds: ArdoqId[],
  consideredWorkspaceIds: ArdoqId[]
) => {
  dispatchAction(justCloseWorkspaces());

  dispatchAction(
    updateLinkedWorkspaces({
      workspaces: presentation.workspaces,
      references: presentation.references,
    })
  );

  const { components, references, tags } = getDataRelevantToWorkspaces(
    presentation,
    new Set(workspaceIds),
    new Set(consideredWorkspaceIds)
  );

  dataInterface.addScopeData({
    models: presentation.models,
    fields: presentation.fields,
    workspaces: presentation.workspaces,
    components,
    references,
    tags,
  });
  dispatchAction(addTags({ tags }));

  workspaceIds.forEach(workspaceId => {
    dispatchAction(markWorkspaceAggregatedLoaded({ workspaceId }));
    dispatchAction(notifyWorkspaceAggregatedLoaded({ workspaceId }));
  });

  const existingWorkspaceIds = workspaceIds.filter(
    workspaceInterface.workspaceExists
  );
  dispatchAction(setWorkspaceIds(existingWorkspaceIds));
};

export const loadSlideState = async (
  presentation: EnhancedPresentation,
  slide: VisualizationSlide,
  loadedWorkspaceIds: ArdoqId[],
  wasInViewpointMode: boolean
) => {
  const transaction = profiling.startTransaction(
    'loading slide state',
    1000,
    profiling.Team.ENGAGEMENT
  );

  dispatchAction(setIsUpdatingAppState(true));

  const wasInScenarioMode = Boolean(getActiveScenarioId());
  const scenarioId = slide.scenarioId || null;
  const isInVisualDiffMode = Boolean(slide.isInVisualDiffMode);
  const isViewpointMode = slideOperations.isViewpointSlide(slide);
  const isScenarioMode = Boolean(scenarioId);
  const workspaceIds = slide.workspaceIds || [];
  const consideredWorkspaceIds = slide.consideredWorkspaceIds || workspaceIds;

  dispatchAction(setScenarioId({ scenarioId }));
  dispatchAction(
    clearLoadedGraphState({ keepCurrentViewPointMode: isViewpointMode })
  );
  requestMainView({
    isViewpointMode,
    activeTabId: toRenderedViewpointViewId(slide.view),
  });

  const wasInLoadedGraphMode = wasInScenarioMode || wasInViewpointMode;
  if (wasInLoadedGraphMode) {
    resetCollectionData();
  }
  if (isScenarioMode) {
    loadScenario(presentation, scenarioId, isInVisualDiffMode);
  } else if (isViewpointMode) {
    if (!tryLoadTraversalData(presentation, slide._id)) {
      dispatchAction(
        clearLoadedGraphState({ keepCurrentViewPointMode: false })
      );
      dispatchAction(setIsUpdatingAppState(false));
      return;
    }
  } else if (wasInLoadedGraphMode) {
    loadMasterData(presentation, workspaceIds, consideredWorkspaceIds);
  } else if (!isCorrectWorkspaces(loadedWorkspaceIds, workspaceIds)) {
    reset(presentation, workspaceIds, consideredWorkspaceIds);
  }
  await postLoad(slide, scenarioId);
  profiling.endTransaction(transaction, {
    metadata: { slideId: slide._id },
  });
};
